import type { HeftProfileView } from '@/services/heft-profile-curation/heft-profile-curation-service'
import type { HeftSubjectType } from '@/services/heft-profile-curation/heft-processing-lens-settings-service'

export type ProcessingLensKey = string

type LensOption = {
    key: string
    label: string
    description: string
    defaultIdentityName: string
    locked?: boolean
}

const PERSON_LENSES: LensOption[] = [
    {
        key: 'professional',
        label: 'Professional',
        description: 'Default — work, expertise, and how they show up professionally.',
        defaultIdentityName: 'Professional',
        locked: true,
    },
    {
        key: 'moral',
        label: 'Moral / values',
        description: 'Values, principles, and decision moments.',
        defaultIdentityName: 'Values & principles',
    },
    {
        key: 'personal_origin',
        label: 'Personal / origin',
        description: 'Origin stories and formative personal context.',
        defaultIdentityName: 'Personal / origin',
    },
    {
        key: 'social',
        label: 'Social',
        description: 'How they relate, collaborate, and show up with others.',
        defaultIdentityName: 'Social',
    },
    {
        key: 'values_beliefs',
        label: 'Values / beliefs',
        description: 'Belief systems and consistency over time.',
        defaultIdentityName: 'Values & beliefs',
    },
]

const COMPANY_LENSES: LensOption[] = [
    {
        key: 'market',
        label: 'Market & category',
        description: 'Category positioning and market narrative.',
        defaultIdentityName: 'Market positioning',
        locked: true,
    },
    {
        key: 'trust',
        label: 'Trust & credibility',
        description: 'Proof, validation, and reasons to believe.',
        defaultIdentityName: 'Trust & proof',
    },
    {
        key: 'employer',
        label: 'Employer brand',
        description: 'Culture, talent, and employee-facing story.',
        defaultIdentityName: 'Employer brand',
    },
    {
        key: 'partner',
        label: 'Partners & ecosystem',
        description: 'Why partners and collaborators work with you.',
        defaultIdentityName: 'Partner narrative',
    },
    {
        key: 'investor',
        label: 'Investors & scale',
        description: 'Traction, governance, and growth narrative.',
        defaultIdentityName: 'Investor narrative',
    },
]

const PRODUCT_LENSES: LensOption[] = [
    {
        key: 'buyer',
        label: 'Buyer evaluation',
        description: 'How evaluators compare and choose this product.',
        defaultIdentityName: 'Buyer evaluation',
        locked: true,
    },
    {
        key: 'comparison',
        label: 'Comparison & alternatives',
        description: 'Differentiation vs alternatives.',
        defaultIdentityName: 'Comparison story',
    },
    {
        key: 'onboarding',
        label: 'Adoption & onboarding',
        description: 'Time-to-value and first wins.',
        defaultIdentityName: 'Adoption narrative',
    },
    {
        key: 'category_education',
        label: 'Category education',
        description: 'Market POV and problem framing.',
        defaultIdentityName: 'Category POV',
    },
]

/** @deprecated use getProcessingLensOptions(subjectType) */
export const PROCESSING_LENS_OPTIONS = PERSON_LENSES

export function getProcessingLensOptions(subjectType: HeftSubjectType = 'person'): LensOption[] {
    if (subjectType === 'company') return COMPANY_LENSES
    if (subjectType === 'product') return PRODUCT_LENSES
    return PERSON_LENSES
}

export function getDefaultLockedLens(subjectType: HeftSubjectType = 'person'): string {
    return getProcessingLensOptions(subjectType).find((l) => l.locked)?.key ?? getProcessingLensOptions(subjectType)[0].key
}

export function formatLensLabel(lensKey: string, subjectType: HeftSubjectType = 'person') {
    return getProcessingLensOptions(subjectType).find((o) => o.key === lensKey)?.label ?? lensKey.replace(/_/g, ' ')
}

export function defaultIdentityNameForLens(lensKey: string, subjectType: HeftSubjectType = 'person') {
    return (
        getProcessingLensOptions(subjectType).find((o) => o.key === lensKey)?.defaultIdentityName ??
        formatLensLabel(lensKey, subjectType)
    )
}

export function viewsForLens(views: HeftProfileView[], lensKey: string) {
    return views.filter((v) => v.lens === lensKey)
}

export function normalizeEnabledLenses(lenses: string[], subjectType: HeftSubjectType = 'person'): string[] {
    const valid = new Set(getProcessingLensOptions(subjectType).map((l) => l.key))
    const locked = getDefaultLockedLens(subjectType)
    const cleaned = (lenses || []).filter((x) => typeof x === 'string' && x.trim() && valid.has(x.trim())).map((x) => x.trim())
    if (!cleaned.includes(locked)) cleaned.unshift(locked)
    return Array.from(new Set(cleaned))
}

export function defaultEnabledLenses(subjectType: HeftSubjectType): string[] {
    if (subjectType === 'company') return ['market', 'trust']
    if (subjectType === 'product') return ['buyer', 'comparison']
    return ['professional']
}

export function subjectWheelCopy(subjectType: HeftSubjectType) {
    if (subjectType === 'company') {
        return {
            centerTitle: 'The company',
            centerHint:
                'Market faces and trust claims describe how the organization shows up. Verify basics in the center card before confirming claims.',
            claimFooter:
                'Trust claims belong to this market face. Click a node to review proof, then confirm or edit.',
            metaFooter: 'Top chart — pick a market face (positioning angle). Breakout — claims for that face only.',
        }
    }
    if (subjectType === 'product') {
        return {
            centerTitle: 'The product',
            centerHint:
                'Buyer narratives and product claims power comparison and recommendation. Link parent company proof when available.',
            claimFooter: 'Product claims for this buyer narrative. Click a node to review proof.',
            metaFooter: 'Pick a buyer narrative, then review claims scoped to that angle.',
        }
    }
    return {
        centerTitle: 'The person',
        centerHint:
            'Personas and claims describe how they show up. Verify basics before curating claims.',
        claimFooter: `Claims belong to this persona. Click a node to review proof for this claim.`,
        metaFooter: 'Top chart — pick a persona. Breakout — claims for that persona only.',
    }
}
